import { AppPaymentTerms } from "@shared_packages/toolkit/types";
import { AppRoutes } from "../../../../constants/routes";
import type { CustomerApiType } from "../../../../util/types";
import {
  buildCreatedByFieldGroup,
  buildRelatedCompanyFieldGroup,
  type DetailCardsConfig,
} from "../../components/detail-cards";

const paymentTermsLabel = (value?: AppPaymentTerms): string | undefined => {
  if (value == null) return undefined;
  return value === AppPaymentTerms.NONE ? "None" : `${value} days`;
};

type PopulatedCompanyAddress = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  zipCode?: string;
  country?: string;
};

type PopulatedCustomerCompany = {
  name?: string;
  websiteUrl?: string;
  businessAddress?: PopulatedCompanyAddress | null;
} | null;

const getCompany = (
  current: CustomerApiType
): PopulatedCustomerCompany | undefined =>
  (current as unknown as { company?: PopulatedCustomerCompany }).company;

const formatCompanyAddress = (
  address?: PopulatedCompanyAddress | null
): string | undefined => {
  if (!address) return undefined;
  return (
    [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.region,
      address.zipCode,
      address.country,
    ]
      .filter(Boolean)
      .join(", ") || undefined
  );
};

export function buildCustomerDetailsCardsConfig(): DetailCardsConfig<CustomerApiType> {
  return {
    cards: [
      {
        title: "Customer details",
        fields: [
          {
            label: "Name",
            displayType: "text",
            accessorKey: "name",
          },
          {
            label: "Email",
            displayType: "email",
            accessorKey: "email",
          },
          {
            label: "Orders count",
            displayType: "text",
            accessorKey: "ordersCount",
          },
          {
            label: "Customer company name",
            displayType: "text",
            getValue: (current) =>
              getCompany(current)?.name ?? current.customerCompanyName,
          },
          {
            label: "Is wholesale",
            displayType: "boolean",
            accessorKey: "isWholesale",
          },
          {
            label: "Is retail",
            displayType: "boolean",
            accessorKey: "isRetail",
          },
          {
            label: "Tax rate",
            displayType: "text",
            accessorKey: "taxRate",
          },
          {
            label: "Website URL",
            displayType: "link",
            getValue: (current) =>
              getCompany(current)?.websiteUrl ?? current.websiteUrl,
            getHref: (current) =>
              getCompany(current)?.websiteUrl ?? current.websiteUrl,
          },
          {
            label: "Business address",
            displayType: "text",
            getValue: (current) =>
              formatCompanyAddress(getCompany(current)?.businessAddress) ??
              current.companyBusinessAddress,
          },
          {
            label: "Tags",
            displayType: "text",
            getValue: (current) =>
              current.tags?.length ? current.tags.join(", ") : undefined,
          },
          {
            label: "Customer code",
            displayType: "text",
            accessorKey: "customerCode",
          },
          {
            label: "Net terms due dates",
            displayType: "text",
            getValue: (current) => paymentTermsLabel(current.netTermsDueDates),
          },
          {
            label: "Quote terms valid dates",
            displayType: "text",
            getValue: (current) =>
              paymentTermsLabel(current.quoteTermsValidDates),
          },
          {
            label: "Phone number",
            displayType: "text",
            getValue: (current) =>
              current.phoneCode && current.phoneNumber
                ? `${current.phoneCode}${current.phoneNumber}`
                : undefined,
          },
        ],
      },
      {
        title: "Creator details",
        fields: [
          ...buildRelatedCompanyFieldGroup<CustomerApiType>("owner", {
            companyDetailsRoute: AppRoutes.Private.Companies.DETAILS,
          }),
          ...buildCreatedByFieldGroup<CustomerApiType>("createdBy", {
            userDetailsRoute: AppRoutes.Private.Users.DETAILS,
          }),
        ],
      },
    ],
  };
}
